import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { selectBootcampPageCompleted } from '~/bootcamp/slices/bootcamp-page.slice'
import { selectPlaying, selectReset } from '~/bootcamp/slices/bootcamp-video.slice'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { GEM_TYPE } from '~/components/QuestPanel/type'
import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { useSetRewardsMutation } from '~/redux/apis/reward-api'
import { useAppSelector } from '~/redux/hooks'
import CircleProgressBar from '~/shared/components/ProgressBar/CircleProgressBar'
import RewardsChest from '~/shared/components/RewardsChest/RewardsChest'
import { AppLanguage } from '~/types/locale'
import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'

export default function BootcampVideoProgressReward({
  percent,
  textColor,
  xp,
}: {
  percent: number
  textColor?: string
  xp: number
}): React.ReactElement {
  const { t } = useTranslation(['generics', 'homebase'])
  const [isChestOpen, setIsChestOpen] = useState(false)
  const [startAnimation, setStartAnimation] = useState(false)
  const [isVideoWatched, setIsVideoWatched] = useState(false)
  const [hasPlayed, setHasPlayed] = useState(false)
  const showClaimButton = percent > 95
  const [setRewards] = useSetRewardsMutation()
  const router = useRouter()
  const isNorwegian = router.locale === AppLanguage.NO
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })
  const hasFinishedVideoPreviously = useAppSelector(selectBootcampPageCompleted)
  const isReset = useAppSelector(selectReset)
  const isPlaying = useAppSelector(selectPlaying)

  useEffect(() => {
    if (isPlaying && !hasPlayed) {
      setHasPlayed(true)
    }
  }, [isPlaying, hasPlayed])

  const onclick = (): void => {
    if (showClaimButton) {
      setIsChestOpen(true)
      setRewards({
        xp,
        kudos: 0,
        gems: 0,
        feature: REWARD_FEATURE.BOOTCAMP,
        event_id: `${REWARD_FEATURE.WATCH_VIDEO}#${router.query.subtopic}#${router.query.key}#${router.query.moduleKey}#${router.query.level}#${router.query.action}`,
      })
    }
  }

  useEffect(() => {
    if (showClaimButton) {
      setTimeout(() => {
        setStartAnimation(true)
      }, 100)
    } else {
      setStartAnimation(false)
    }
  }, [showClaimButton])

  useEffect(() => {
    if (isReset) {
      setIsChestOpen(false)
      setIsVideoWatched(false)
    }
  }, [isReset])

  useEffect(() => {
    if (hasFinishedVideoPreviously && !hasPlayed) {
      setIsVideoWatched(true)
    } else {
      setIsVideoWatched(false)
    }
  }, [hasPlayed, hasFinishedVideoPreviously])

  return (
    <div
      className={`flex w-auto h-full justify-end md:justify-center items-center md:ml-2 -mt-6 mb-2 md:mt-2 md:bg-purple-darker md:border md:border-yellow-vibrant rounded ${
        showClaimButton && !isChestOpen ? 'ml-16 md:ml-4 mt-2 md:mt-8' : 'md:flex-col-reverse'
      } ${isChestOpen ? 'md:py-6' : showClaimButton ? '' : 'py-2'}`}
    >
      <div
        className={`h-19 ${showClaimButton && !isChestOpen ? '-mr-8 -ml-6' : ''} ${
          isChestOpen ? '' : isVideoWatched ? 'px-2' : showClaimButton ? '-mt-10' : '-mt-6'
        }`}
      >
        {isVideoWatched ? (
          <ResponsiveImage
            alt="Treasure Chest"
            fit="contain"
            height={75}
            imageClassName="w-12 md:w-19"
            imageWidths="300"
            srcKey="onboarding/icons/updated-chest-open.png"
            width={75}
          />
        ) : (
          <RewardsChest
            hasBackgroundBubble={false}
            rewards={{ xp, kudos: 0, gemType: GEM_TYPE.RED, gemCount: 0 }}
            rewardSize="medium"
            size="x-small"
            startAnimation={isChestOpen}
          />
        )}
      </div>
      {showClaimButton && !isChestOpen ? (
        <div
          className={`pr-2 pt-2 pb-3 my-auto ${
            isMobile
              ? 'pl-16 -ml-8 mb-1 border-2 border-yellow-vibrant bg-gradient-lightblue-purple rounded shadow-task-purple-dark'
              : 'pl-8'
          } ${startAnimation ? 'opacity-100 duration-500' : 'opacity-0 duration-300'}`}
        >
          <button
            className="bg-orange md:bg-purple text-black md:text-white shadow-task-light-orange md:shadow-task-purple-light rounded md:py-1 px-2 hover:scale-105 cursor-pointer duration-300 font-semibold capitalize"
            onClick={onclick}
          >
            {isNorwegian ? t('homebase:open') : t('generics:claim')}
          </button>
        </div>
      ) : (
        <>
          {!isChestOpen && !isVideoWatched && (
            <CircleProgressBar
              colorStart={isMobile ? '#FFA336' : '#9F7CFF'}
              colorStop={isMobile ? '#E17A00' : '#7945FF'}
              percentage={percent}
              textColor={textColor}
            />
          )}
        </>
      )}
    </div>
  )
}
